import { Fragment } from 'react';

import { Button } from '@components/button';
import { useConfirmModal, useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconLoop } from '@components/icons/loop';
import { IconPause } from '@components/icons/pause';
import { IconPlay } from '@components/icons/play';
import { IconResume } from '@components/icons/resume';
import { IconTrash } from '@components/icons/trash';
import { PopoverSelect } from '@components/popover-select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { SelectionBanner } from '@components/table/selection-banner';
import { pxToRem } from '@utils/converters';

import { useIsOutOfCredits } from 'api/authentication/hooks';
import { useJourneys } from 'api/journeys/hooks';
import {
  useBulkDeleteTravellerJourney,
  useBulkPauseTravellerJourney,
  useBulkResetTravellerJourney,
  useBulkResumeTravellerJourney,
  useBulkStartTravellerJourney,
} from 'api/traveller-journeys/hooks';
import { TravellerJourney, TravellerJourneyListItem } from 'api/types';
import { BulkTravellerModalCount } from 'components/bulk-traveller-modal-count';
import { useTravellerJourneyListTranslate } from 'ui/authenticated/main/pages/journeys/traveller-journey-list/locale';

import { useBulkCompanyTravellerToJourneySidebar } from '../company-travellers/sidebars/add-to-journey';

export const SelectionHeader = ({
  journeyId,
  selectedTravellers,
  clearSelectedTravellers,
}: {
  journeyId: string;
  selectedTravellers: Array<TravellerJourney | TravellerJourneyListItem>;
  clearSelectedTravellers(): void;
}) => {
  const { t } = useTravellerJourneyListTranslate();
  const journeysQuery = useJourneys('?ordering=title');
  const isOutOfCredits = useIsOutOfCredits();
  const showDeleteModal = useDeleteModal();
  const showConfirmModal = useConfirmModal();
  const bulkOptions = {
    onSuccess: clearSelectedTravellers,
  };
  const bulkStartMutation = useBulkStartTravellerJourney(journeyId, bulkOptions);
  const bulkDeleteMutation = useBulkDeleteTravellerJourney(journeyId, bulkOptions);
  const bulkPauseMutation = useBulkPauseTravellerJourney(journeyId, bulkOptions);
  const bulkResumeMutation = useBulkResumeTravellerJourney(journeyId, bulkOptions);
  const bulkResetMutation = useBulkResetTravellerJourney(journeyId, bulkOptions);
  const showAddToJourneySidebar = useBulkCompanyTravellerToJourneySidebar();
  const notStartTravellers = selectedTravellers.filter(
    (traveller) => traveller.status === 'not_started'
  );
  const resetTravellers = selectedTravellers.filter(
    (traveller) => traveller.status !== 'not_started'
  );
  const inProgressTravellers = selectedTravellers.filter(
    (traveller) => traveller.status === 'in_progress'
  );
  const pausedTravellers = selectedTravellers.filter((traveller) => traveller.status === 'paused');

  const data = {
    total: selectedTravellers.length,
    notStarted: notStartTravellers.length,
    inProgress: inProgressTravellers.length,
    paused: pausedTravellers.length,
    completed: selectedTravellers.filter((traveller) => traveller.status === 'completed').length,
    toBeStarted: notStartTravellers.length,
    toBePaused: inProgressTravellers.length,
    toBeResumed: pausedTravellers.length,
    toBeReset: resetTravellers.length,
    toBeRemoved: selectedTravellers.length,
  };
  const companyTravellers = selectedTravellers.map((traveller) => ({
    companyTravellerShortId: traveller.companyTravellerShortId,
  }));

  const journeyListOptions = (journeysQuery.data ?? [])
    .filter((journey) => journey.hasPublishedJourney)
    .map((journey) => ({
      value: journey.shortId,
      label: journey.title,
      helpText: journey.description || t('noDescription'),
    }));

  return (
    <SelectionBanner
      selectedCount={selectedTravellers.length}
      clearSelected={clearSelectedTravellers}
    >
      <Stack orientation="horizontal" space={4}>
        {data.toBeStarted > 0 && (
          <Button
            iconLeft={<IconPlay />}
            text={t('startJourney')}
            isDisabled={isOutOfCredits}
            variant="ghost"
            onClick={() =>
              showConfirmModal({
                title: t('startBulkTitle'),
                buttonText: t('start'),
                buttonIcon: <IconPlay />,
                buttonVariant: 'primary-blue',
                hideButton: data.toBeStarted === 0,
                onConfirm: () =>
                  bulkStartMutation.mutate({
                    ids: notStartTravellers.map((traveller) => traveller.shortId),
                  }),
                children: <BulkTravellerModalCount type="start" {...data} />,
              })
            }
          />
        )}
        {data.toBePaused > 0 && (
          <IconButton
            icon={<IconPause />}
            variant="ghost-purple"
            tooltip={t('pauseJourney')}
            disabled={isOutOfCredits}
            onClick={() =>
              showConfirmModal({
                title: t('pauseBulkTitle'),
                buttonText: t('pause'),
                buttonIcon: <IconPause />,
                hideButton: data.toBePaused === 0,
                onConfirm: () =>
                  bulkPauseMutation.mutate({
                    ids: inProgressTravellers.map((traveller) => traveller.shortId),
                  }),
                children: <BulkTravellerModalCount type="pause" {...data} />,
              })
            }
          />
        )}
        {data.toBeResumed > 0 && (
          <IconButton
            icon={<IconResume />}
            variant="ghost-purple"
            tooltip={t('resumeJourney')}
            disabled={isOutOfCredits}
            onClick={() =>
              showConfirmModal({
                title: t('resumeBulkTitle'),
                buttonText: t('resume'),
                buttonIcon: <IconPlay />,
                hideButton: data.toBeResumed === 0,
                onConfirm: () =>
                  bulkResumeMutation.mutate({
                    ids: pausedTravellers.map((traveller) => traveller.shortId),
                  }),
                children: <BulkTravellerModalCount type="resume" {...data} />,
              })
            }
          />
        )}
        {data.toBeReset > 0 && (
          <IconButton
            icon={<IconLoop />}
            variant="ghost-purple"
            tooltip={t('resetJourney')}
            disabled={isOutOfCredits}
            onClick={() =>
              showDeleteModal({
                title: t('resetBulkTitle'),
                description: t('bulkResetDescription'),
                hasUserConfirmation: true,
                buttonText: t('reset'),
                buttonIcon: <IconLoop />,
                onConfirm: () =>
                  bulkResetMutation.mutate({
                    ids: resetTravellers.map((traveller) => traveller.shortId),
                  }),
                children: <BulkTravellerModalCount type="reset" {...data} />,
              })
            }
          />
        )}
        {data.total > 0 && (
          <Fragment>
            <Separator orientation="horizontal" />
            <PopoverSelect
              trigger={
                <Button
                  variant="ghost"
                  text={t('addToJourney')}
                  dataTestid="add-to-journey-button-topbar"
                  iconLeft={<IconJourneyItem />}
                />
              }
              width={pxToRem(400)}
              maxHeight={464}
              items={journeyListOptions}
              searchPlaceholder={`${t('addToJourney')}...`}
              clearSelected
              onChange={(selectedOptions) => {
                selectedOptions.length > 0 &&
                  showAddToJourneySidebar({
                    journeyShortId: selectedOptions[0],
                    selectedTravellers: companyTravellers,
                  });
              }}
              placement="bottom-start"
            />
          </Fragment>
        )}
        {data.toBeRemoved > 0 && (
          <Fragment>
            <Separator orientation="horizontal" />
            <IconButton
              icon={<IconTrash />}
              variant="danger"
              tooltip={t('deleteTravellers')}
              disabled={isOutOfCredits}
              onClick={() =>
                showDeleteModal({
                  title: t('deleteBulkTitle'),
                  description: t('bulkDeleteDescription'),
                  hasUserConfirmation: true,
                  buttonText: t('core.remove'),
                  onConfirm: () =>
                    bulkDeleteMutation.mutate({
                      ids: selectedTravellers.map((traveller) => traveller.shortId),
                    }),
                  children: <BulkTravellerModalCount type="remove" {...data} />,
                })
              }
            />
          </Fragment>
        )}
      </Stack>
    </SelectionBanner>
  );
};
